import { useState, type ReactNode } from 'react'
import type { PlatformAccounts, Profile } from '../../api'
import type { Destination } from '../types'
import { EditButton, LogoSlot, RelayToggle } from '../ui'
import { EyeIcon, viewersText } from '../PlatformBlock'
import { t } from '../../i18n'
import { filterOutputs, fixKind, needsYou, rowStatus, rowTone, sortOutputs, type OutputsFilter, type Tone } from './model'
import { KeyFix } from './Tips'
import { ChatIcon } from './StudioBar'
import { ProtocolChip } from '../ProtocolSettings'
import { ixOf } from '../tracks'
import { Button, Icon, Segmented, Tag } from '../../ui'
import { dualPairs } from '../vertical'
import type { AccountPlatform } from './useLiveScreen'
import { Link } from 'react-router'
import { useSession } from '../../session'
import { BILLING_PATH, hasPlan } from '../../billing'

export type RightMode = 'outs' | 'chats'

const DOT: Record<Tone, string> = { live: 'bg-live', start: 'bg-start', warn: 'bg-warn', fail: 'bg-fail', idle: 'border border-idle' }

/** Outputs | Chats: two big buttons (O / C), each keeps the main thing of the other. */
export function ModeSwitch({ mode, onMode, dests, chats, chatApi, unread = 0 }: {
  mode: RightMode; onMode: (m: RightMode) => void; dests: Destination[]; chats: number;
  /** the center reads chats (outputs carry `chat`): count the chats read, show unread */
  chatApi?: boolean; unread?: number;
}) {
  const on = dests.filter((d) => d.enabled)
  const need = dests.filter(needsYou).length
  const live = dests.filter((d) => d.state === 'live').length
  const btn = (m: RightMode, label: string, meta: ReactNode, badge?: ReactNode) => (
    <button type="button" role="tab" aria-selected={mode === m} data-testid={`mode-${m === 'outs' ? 'outputs' : 'chats'}`} onClick={() => onMode(m)}
      title={`${label} (${m === 'outs' ? 'O' : 'C'})`} aria-keyshortcuts={m === 'outs' ? 'O' : 'C'}
      className={`flex min-h-12 flex-col items-start justify-center px-3 text-left ${mode === m ? 'shadow-[inset_0_-2px_0_var(--accent)]' : 'hover:bg-line/40'}`}>
      <span className="flex w-full items-center gap-2"><span className={`text-sm font-medium ${mode === m ? 'text-text' : 'text-text-soft'}`}>{label}</span><span className="ml-auto">{badge}</span></span>
      {meta}
    </button>
  )
  return (
    // the system's tabs (.sx-tabs): the open one has the 2 px accent edge below (CB-08)
    <div role="tablist" aria-label={t('right.label')} data-testid="right-switch" className="grid flex-none grid-cols-2 border-b border-line">
      {btn('outs', t('right.outputs'), <span className="num font-mono text-[11px] text-muted">{t('right.live', { live, n: on.length })}</span>,
        need ? <span className="inline-flex items-center gap-1 font-mono text-[11px] text-warn"><Icon name="state-warn" size={12} />{need}</span> : undefined)}
      {btn('chats', t('right.chats'), <span className="num font-mono text-[11px] text-muted">{chatApi ? t('right.chatsN', { n: chats }) : t('right.chatsSoon', { n: chats })}</span>,
        chatApi && mode !== 'chats' && unread > 0
          ? <span data-testid="mode-chats-unread" aria-label={t('chat.unread', { n: unread })} className="num rounded-full bg-accent/[.14] px-1.5 font-mono text-[11px] text-accent">{unread > 999 ? '999+' : unread}</span>
          : undefined)}
    </div>
  )
}

/** One output, ≈52 px: dot, logo, name, one status line, viewers, toggle, settings; a quick fix for a problem. */
/** The captions chip of an output row (YouTube with Send captions on). */
/** The transcoding line of an output row (TRANS-1). */
const TC_TONE = { live: 'text-live', start: 'text-start', warn: 'text-warn', muted: 'text-muted' } as const
const CC_LOOK = { live: 'border-live/50 text-live', start: 'border-start/50 text-start', warn: 'border-warn/50 text-warn', fail: 'border-fail/50 text-fail', idle: 'border-slot text-muted' } as const

export function OutputRow({ d, now, saved, phone, editing, profile, unread, onToggle, onEdit, onSaveKey, onChat }: {
  d: Destination; now: number; saved?: 'reconnect' | 'fill'; phone?: boolean; editing?: boolean; profile?: Profile;
  /** unread chat items of this output; undefined = its chat is not read */
  unread?: number;
  onToggle: () => void; onEdit: () => void; onSaveKey: (id: number, key: string) => Promise<void>; onChat?: () => void;
}) {
  const [keyOpen, setKeyOpen] = useState(false)
  const st = rowStatus(d, now)
  const tone = rowTone(d)
  const kind = fixKind(d)
  const planLink = hasPlan(useSession().user)
  // the system's output row (OutputCard): state icon, platform tile, name, rate, "platform · state",
  // on/off switch, settings; a 2 px edge in the state's colour (CB-06)
  return (
    <li data-testid={`dest-${d.id}`} data-state={d.apiState} aria-label={`${d.name} on ${d.profileName}`}
      className={`flex flex-col gap-1.5 border-b border-line pb-2 ${!d.enabled ? 'opacity-60' : ''} ${kind ? 'bg-warn/[.04]' : ''} ${editing ? 'shadow-[inset_2px_0_0_var(--accent)]' : ''}`}>
      <div className="sx-output min-h-0 border-b-0 bg-transparent pb-0" data-state={tone}>
        <span className="st"><Icon name={`state-${tone}`} size={12} className={tone === 'start' ? 'sx-turn' : undefined} /></span>
        <LogoSlot platform={d.platform} dim={!d.enabled} />
        <div className="nm flex min-w-0 items-center gap-1.5">
          <span title={d.name} className="truncate">{d.name}</span>
          <ProtocolChip protocol={d.protocol} platformLabel={d.profileName} testId={`row-protocol-${d.id}`} />
          {d.needsKey && <span data-testid="needs-key-dot" title={t('row.needsKey')} className="inline-flex flex-none text-warn"><Icon name="key" size={12} /></span>}
        </div>
        <div className="rate">
        {d.slate && (
          <span data-testid="slate-chip" title={t('slate.chipTitle')}
            className="flex-none whitespace-nowrap rounded-md border border-dashed border-warn/45 px-[5px] py-px font-mono text-[10px] font-medium tracking-[.06em] text-warn">{t('slate.chip')}</span>
        )}
        {d.captions && (
          <span data-testid="captions-chip" data-color={d.captions.color} title={t('captions.title')}
            className={`flex-none whitespace-nowrap rounded-md border px-[5px] py-px font-mono text-[10px] font-medium tracking-[.06em] ${CC_LOOK[d.captions.color]}`}>{d.captions.text}</span>
        )}
        {unread !== undefined && unread > 0 && onChat && (
          <button type="button" data-testid={`row-chat-${d.id}`} onClick={onChat} title={t('chat.openOf', { name: d.name })} aria-label={`${t('chat.openOf', { name: d.name })}: ${t('chat.unread', { n: unread })}`}
            className="num flex min-h-7 flex-none items-center gap-1 rounded-md px-1 font-mono text-[11px] text-accent hover:bg-accent/[.08] max-md:min-h-[40px]">
            <ChatIcon size={13} />{unread > 999 ? '999+' : unread}
          </button>
        )}
        {d.viewers !== undefined && (
          <span data-testid={`row-viewers-${d.id}`} className="num flex items-center gap-1 font-mono text-[11px] text-text-soft" title={t('row.viewers')}><EyeIcon />{viewersText(d.viewers)}</span>
        )}
        </div>
        <div data-testid="dest-state" title={d.note ?? st.text} className="sub line-clamp-2">
          {d.profileName} · <b>{st.text}</b>
          {d.tracks && !d.tracks.note && <span data-testid={`row-tracks-${d.id}`} className="font-mono"> · {ixOf('video', d.tracks.video)} · {ixOf('audio', d.tracks.audio)}</span>}
          <VerticalBits d={d} />
        </div>
        <RelayToggle on={d.enabled} busy={d.busy} size={phone ? 'touch' : 'md'}
          label={`${d.name}: ${d.enabled ? 'turn off' : 'turn on'}`} onToggle={onToggle} />
        <div className="act"><EditButton editing={!!editing} attention={d.needsKeyFix || d.needsKey} label={d.name} onClick={onEdit} /></div>
      </div>
      {d.portraitLandscape && (
        // DS: VT-23 — owner 02.11: what a 9:16 platform gets goes as is; a warn line under the row, as the transcoding line
        <div data-testid={`dest-landscape-${d.id}`} className="flex items-start gap-1 pl-[70px] text-[11px] text-warn">
          <Icon name="state-warn" size={12} className="mt-px flex-none" />
          <span>{t('row.portraitLandscape', { platform: d.profileName })}</span>
        </div>
      )}
      {d.transcode && (
        <div data-testid={`dest-tc-${d.id}`} data-state={d.transcode.state} className="flex flex-col gap-0.5 pl-[70px] text-[11px]">
          {d.transcode.label && <span data-testid={`dest-tc-label-${d.id}`} className={`truncate ${TC_TONE[d.transcode.tone]}`} title={d.transcode.label}>{d.transcode.label}</span>}
          {d.transcode.notes.map((n) => (
            <span key={n.code} data-testid={`dest-tc-note-${d.id}-${n.code}`} className={n.tone === 'warn' ? 'text-warn' : 'text-muted'}>
              {n.tone === 'warn' && <Icon name="state-warn" size={12} className="mr-1 inline align-[-1px]" />}{n.text}
            </span>
          ))}
        </div>
      )}
      {d.mark && (
        // WM3 (WMU-02): the OutputCard's second-line look (as transcoding and captions delivery), muted — the mark breaks nothing
        <div data-testid={`dest-wm-${d.id}`} title={d.mark.title} className="flex flex-wrap items-center gap-x-3 pl-[70px] text-[11px] text-muted">
          <span data-testid={`dest-wm-label-${d.id}`}>{d.mark.before}{d.mark.res && <span className="num font-mono">{d.mark.res}</span>}{d.mark.after}</span>
          {planLink && (
            <Link to={BILLING_PATH} data-testid={`dest-wm-plan-${d.id}`} className="text-accent no-underline max-md:inline-flex max-md:min-h-[44px] max-md:items-center">{t('plan.seePlan')} →</Link>
          )}
        </div>
      )}
      {d.captionsDelivery && (
        <div data-testid={`dest-cc-${d.id}`} data-method={d.captionsDelivery.method} data-state={d.captionsDelivery.state} className="flex flex-col gap-0.5 pl-[70px] text-[11px]">
          {d.captionsDelivery.label && <span data-testid={`dest-cc-label-${d.id}`} className={`truncate ${TC_TONE[d.captionsDelivery.tone]}`} title={d.captionsDelivery.label}>{d.captionsDelivery.label}</span>}
          {d.captionsDelivery.notes.map((n) => (
            <span key={n.code} data-testid={`dest-cc-note-${d.id}-${n.code}`} className={n.tone === 'warn' ? 'text-warn' : 'text-muted'}>
              {n.tone === 'warn' && <Icon name="state-warn" size={12} className="mr-1 inline align-[-1px]" />}{n.text}
            </span>
          ))}
        </div>
      )}
      {saved && (
        <div role="status" data-testid={`dest-saved-${d.id}`} className="pl-[70px] text-[11px] text-live">
          {saved === 'fill' ? t('row.savedNoReconnect') : t('row.savedReconnect', { name: d.name })}
        </div>
      )}
      {kind === 'key' && (
        keyOpen ? (
          <div className="pl-[70px]"><KeyFix dest={d} profile={profile} autoFocus onSave={async (id, k) => { await onSaveKey(id, k); setKeyOpen(false) }} testId="row-key" /></div>
        ) : (
          <div className="flex gap-1.5 pl-[70px]">
            <Button size="sm" data-testid={`row-paste-key-${d.id}`} onClick={() => setKeyOpen(true)} className="max-md:min-h-[40px]">
              <Icon name="key" size={16} />{t('row.pasteKey')}
            </Button>
          </div>
        )
      )}
      {kind === 'track' && (
        <div className="flex gap-1.5 pl-[70px]">
          <Button size="sm" data-testid={`row-change-track-${d.id}`} onClick={onEdit} className="max-md:min-h-[40px]">{t('row.changeTrack')}</Button>
        </div>
      )}
      {kind === 'settings' && (
        <div className="flex gap-1.5 pl-[70px]">
          <Button size="sm" onClick={onEdit} className="max-md:min-h-[40px]">{t('row.checkServer')}</Button>
        </div>
      )}
    </li>
  )
}

/** VERT-1 in an output's line (mockup A, B1, B2, F; VT-11): the source before the broadcast, then the neutral 9:16 tag. */
export function VerticalBits({ d }: { d: Destination }) {
  const v = d.vertical
  if (!v) return null
  const words = v.state ? undefined : v.label === 'auto' ? t('vert.out.auto') : v.label === 'vertical' ? t('vert.out.vertical') : v.label === 'main' ? t('vert.row.main') : undefined
  return (
    <span data-testid={`row-vertical-${d.id}`} data-label={v.label ?? ''} data-state={v.state ?? ''}>
      {words && <> · {words}</>}
      {v.tag && <> · <Tag>{t('vert.tag')}</Tag></>}
    </span>
  )
}

/** The name of a Dual stream card: the destinations are "<name> · horizontal" / "· vertical" (POST youtube-dual). */
const dualName = (n: string) => n.replace(/\s·\s(horizontal|vertical)$/i, '')

/** A leg's state in a word (mockup A, F): Live, Waits for OBS, else the row's own line. */
function legWord(d: Destination, now: number): { text: string; tone: Tone } {
  const st = rowStatus(d, now)
  if (d.state === 'live' && st.tone === 'live') return { text: t('out.live'), tone: 'live' }
  if (d.state === 'waiting' && !d.needsKey && !d.vertical?.state) return { text: t('out.waits'), tone: 'idle' }
  return st
}

/**
 * YouTube Dual stream (mockup A, B, F, G; VT-02, owner 01.11): one output card for the broadcast — the platform,
 * the name, "YouTube · Dual stream", settings — and two legs, Horizontal and Vertical 9:16, each with its state,
 * rate and its own switch (they turn on and off apart, docs/vertical.md §4.3), then the link the user makes in
 * YouTube Studio, which we can't check.
 */
export function DualCard({ h, v, now, phone, editing, onToggle, onEdit }: {
  h: Destination; v: Destination; now: number; phone?: boolean; editing?: number;
  onToggle: (d: Destination) => void; onEdit: (id: number) => void;
}) {
  const legs: [Destination, 'h' | 'v'][] = [[h, 'h'], [v, 'v']]
  const words = legs.map(([d]) => legWord(d, now))
  const tone: Tone = words.some((w) => w.tone === 'fail') ? 'fail' : words.some((w) => w.tone === 'warn') ? 'warn' : words.some((w) => w.tone === 'live') ? 'live' : words.some((w) => w.tone === 'start') ? 'start' : 'idle'
  // settings open the leg that needs the streamer, else the horizontal one
  const target = needsYou(v) && !needsYou(h) ? v : h
  return (
    // DS: VT-02 — the dual state of OutputCard: one card, two legs with their own .sx-toggle.sm
    <li data-testid={`dual-${h.id}`} data-state={tone} aria-label={`${dualName(h.name)} · ${t('vert.dual.sub')}`}
      className={`flex flex-col border-b border-line pb-2 ${editing === h.id || editing === v.id ? 'shadow-[inset_2px_0_0_var(--accent)]' : ''}`}>
      <div className="sx-output min-h-0 border-b-0 bg-transparent pb-0" data-state={tone}>
        <span className="st"><Icon name={`state-${tone}`} size={12} className={tone === 'start' ? 'sx-turn' : undefined} /></span>
        <LogoSlot platform={h.platform} dim={!h.enabled && !v.enabled} />
        <div className="nm min-w-0 truncate" title={dualName(h.name)}>{dualName(h.name)}</div>
        <div className="sub">{t('vert.dual.sub')}</div>
        <div className="act"><EditButton editing={editing === target.id} attention={target.needsKeyFix || target.needsKey} label={dualName(h.name)} onClick={() => onEdit(target.id)} /></div>
      </div>
      {legs.map(([d, k], i) => {
        const w = words[i]!
        return (
          // DS: VT-02 — a leg: state icon, which half, its state, rate, its own switch
          <div key={d.id} data-testid={`dual-leg-${d.id}`} data-leg={k} data-state={d.apiState} data-tone={w.tone}
            className={`grid min-h-8 grid-cols-[12px_minmax(0,1fr)_auto_auto] items-center gap-x-2 pr-3 text-xs ${phone ? 'pl-12' : 'pl-[58px]'} ${!d.enabled ? 'opacity-60' : ''}`}>
            <span className={`grid place-items-center ${TC_TONE_LEG[w.tone]}`}><Icon name={`state-${w.tone}`} size={12} className={w.tone === 'start' ? 'sx-turn' : undefined} /></span>
            <span className="flex min-w-0 items-center gap-1.5 text-text-soft">
              <span className="whitespace-nowrap">{k === 'h' ? t('vert.dual.h') : t('vert.dual.v')}</span>
              {k === 'v' && <Tag>{t('vert.tag')}</Tag>}
              <span className="text-faint" aria-hidden>·</span>
              <b className={`min-w-0 truncate font-normal ${TC_TONE_LEG[w.tone]}`} title={d.note ?? w.text}>{w.text}</b>
            </span>
            <span className="num font-mono text-xs text-text-soft">{d.state === 'live' && d.bitrateMbps !== undefined ? `${d.bitrateMbps.toFixed(1)} Mbps` : ''}</span>
            <RelayToggle on={d.enabled} busy={d.busy} size="sm" testId={`dual-toggle-${d.id}`} label={`${dualName(h.name)} ${k === 'h' ? t('vert.dual.h') : t('vert.dual.v')}: ${d.enabled ? 'turn off' : 'turn on'}`} onToggle={() => onToggle(d)} />
          </div>
        )
      })}
      <p className={`m-0 pr-3 text-xs text-muted ${phone ? 'pl-12' : 'pl-[58px]'}`}>{t('vert.dual.link')}</p>
    </li>
  )
}

const TC_TONE_LEG: Record<Tone, string> = { live: 'text-live', start: 'text-start', warn: 'text-warn', fail: 'text-fail', idle: 'text-muted' }

/** Outputs mode: search, filter, the list (waiting for you → the rest → off), Add output. */
export function OutputsList({ dests, now, phone, justSaved, editing, profiles, max, unread, onToggle, onEdit, onSaveKey, onAdd, onChat }: {
  dests: Destination[]; now: number; phone?: boolean; justSaved: { id: number; kind: 'reconnect' | 'fill' } | null; editing?: number;
  profiles?: Profile[]; max: number;
  /** unread chat items by output id */
  unread?: Record<string, number>;
  onToggle: (d: Destination) => void; onEdit: (id: number) => void; onSaveKey: (id: number, key: string) => Promise<void>; onAdd: () => void;
  /** open Chats filtered to this output */
  onChat?: (d: Destination) => void;
}) {
  const [filter, setFilter] = useState<OutputsFilter>('all')
  const [q, setQ] = useState('')
  const need = dests.filter(needsYou).length
  const list = filterOutputs(sortOutputs(dests), filter, q)
  const duals = dualPairs(dests)
  const drawn = new Set<number>()
  const full = dests.length >= max
  const { user } = useSession()
  const near = dests.length >= max * 0.9
  const FILTERS: [OutputsFilter, string][] = [['all', t('outs.all')], ['need', need ? `${t('outs.need')} ${need}` : t('outs.need')], ['live', t('outs.live')], ['off', t('outs.off')]]
  return (
    <div data-testid="outputs" className="flex min-h-0 flex-1 flex-col">
      {dests.length > 0 && (
        <div className="flex flex-none flex-col gap-2 border-b border-line p-3">
          <label className="sx-input sans gap-2 pr-2 text-[13px] text-muted focus-within:border-accent focus-within:shadow-focus max-md:min-h-[44px]">
            <Icon name="search" size={16} />
            <span className="sr-only">{t('outs.search')}</span>
            <input data-testid="outputs-search" type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t('outs.search')}
              className="h-full w-full bg-transparent text-text outline-none placeholder:text-muted" />
          </label>
          <Segmented label={t('outs.show')} value={filter} onChange={setFilter} className="self-start max-md:[&>span]:min-h-[40px]"
            options={FILTERS.map(([k, l]) => ({ value: k, label: l, testId: `outputs-filter-${k}`, className: k === 'need' && need && filter !== k ? 'text-warn' : undefined }))} />
        </div>
      )}
      <ul data-testid="outputs-list" aria-label={t('right.outputs')} className="m-0 min-h-0 flex-1 list-none overflow-y-auto p-0">
        {dests.length === 0 && <li className="p-4 text-[13px] text-text-soft">{t('outs.empty')}</li>}
        {dests.length > 0 && list.length === 0 && <li data-testid="outputs-none" className="p-4 text-[13px] text-muted">{t('outs.none')}</li>}
        {list.map((d) => {
          // VERT-1: the two outputs of a YouTube Dual stream are one card, where the first of them stands
          const pair = duals.pairs.find((p) => p.horizontal.id === d.id || p.vertical.id === d.id)
          if (pair) {
            if (drawn.has(pair.horizontal.id)) return null
            drawn.add(pair.horizontal.id)
            return <DualCard key={`dual-${pair.horizontal.id}`} h={pair.horizontal} v={pair.vertical} now={now} phone={phone} editing={editing} onToggle={onToggle} onEdit={onEdit} />
          }
          return (
          <OutputRow key={d.id} d={d} now={now} phone={phone} editing={editing === d.id} profile={profiles?.find((p) => p.id === d.profile)}
            saved={justSaved?.id === d.id ? justSaved.kind : undefined}
            unread={d.chat?.access === 'reads' ? (unread?.[d.outputId] ?? 0) : undefined}
            onToggle={() => onToggle(d)} onEdit={() => onEdit(d.id)} onSaveKey={onSaveKey} onChat={onChat ? () => onChat(d) : undefined} />
          )
        })}
      </ul>
      <div className="flex flex-none flex-col gap-1.5 border-t border-line p-3">
        <Button variant="primary" data-testid="add-output" disabled={full} onClick={onAdd} className="w-full max-md:min-h-[44px]">
          <Icon name="plus" size={16} />{t('outs.add')}
        </Button>
        {/* the plan's count as the system's meter: one segment per output, warn from 90 % (PlansAndLimits, CB-21) */}
        <div className="sx-meter min-w-0" data-state={near ? 'warn' : ''}>
          <div className="top"><span data-testid="outputs-limit">{full ? t('outs.full', { max }) : t('outs.limit', { n: dests.length, max })}</span></div>
          {max <= 50 && <div className="trk">{Array.from({ length: max }, (_, i) => <i key={i} className={i < dests.length ? 'u' : ''} />)}</div>}
          {/* DS: PL-04 — at the limit: the next step (PlansAndLimits «100 %: what the next step needs + Compare plans») */}
          {near && hasPlan(user) && (
            <Link to={BILLING_PATH} data-testid="outputs-plan" className="text-xs text-accent no-underline">{t('plan.seePlan')} →</Link>
          )}
        </div>
      </div>
    </div>
  )
}

export type ChatAccess = 'reads' | 'connect' | 'unsupported' | 'unknown'

/** Who will be able to read chat, from the accounts the user connected (chats themselves: stage 3). */
export function chatAccess(d: Destination, acc?: PlatformAccounts): ChatAccess {
  if (d.platform !== 'youtube' && d.platform !== 'twitch') return 'unsupported'
  if (!acc) return 'unknown'
  if (acc.accounts.some((a) => a.platform === d.platform)) return 'reads'
  return acc.available.includes(d.platform) ? 'connect' : 'unsupported'
}

/** Chats mode: every output's state (the broadcast stays in view) and the one that dropped with Fix →. */
export function WatchStrip({ dests, now, onOutputs, onFix }: { dests: Destination[]; now: number; onOutputs: () => void; onFix: (d: Destination) => void }) {
  const on = dests.filter((d) => d.enabled)
  const bad = on.filter(needsYou)
  const worst = bad.find((d) => d.state === 'failed') ?? bad[0]
  return (
    <div data-testid="watch-strip" className="flex flex-none flex-col border-b border-line">
      <div className="flex items-center gap-1.5 px-3 py-2">
        <span className="font-mono text-[10px] tracking-[.1em] text-muted">{t('chats.outputs')}</span>
        <span className="flex flex-wrap items-center gap-1">
          {on.map((d) => (
            <button key={d.id} type="button" onClick={onOutputs} title={`${d.name}: ${rowStatus(d, now).text}`} data-testid={`watch-${d.id}`} data-tone={rowTone(d)} className="relative">
              <LogoSlot platform={d.platform} dim={d.state !== 'live'} />
              <span aria-hidden className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-panel ${DOT[rowTone(d)]}`} />
            </button>
          ))}
        </span>
      </div>
      {worst && (
        <button type="button" data-testid="watch-fix" onClick={() => onFix(worst)}
          className={`flex items-center gap-2 px-3 py-1.5 text-left text-xs ${worst.state === 'failed' ? 'bg-fail/[.08] text-fail' : 'bg-warn/[.08] text-warn'}`}>
          <Icon name={worst.state === 'failed' ? 'state-fail' : 'state-warn'} size={12} />
          <span className="min-w-0 flex-1 truncate">{worst.state === 'live' ? t('chats.needsYou', { name: worst.name }) : t('chats.offAir', { name: worst.name })}</span>
          <span className="inline-flex items-center gap-1 font-medium">{t('chats.fix')}<Icon name="chevron-right" size={12} /></span>
        </button>
      )}
    </div>
  )
}

/**
 * Chats mode before chats exist (stage 3): a strip of every output's state (the
 * broadcast stays in view), the one that dropped with Fix →, and which outputs will
 * read chat once it comes.
 */
export function ChatsSoon({ dests, accounts, now, connecting, onConnect, onOutputs, onFix }: {
  dests: Destination[]; accounts?: PlatformAccounts; now: number; connecting: AccountPlatform | null;
  onConnect: (p: AccountPlatform) => void; onOutputs: () => void; onFix: (d: Destination) => void;
}) {
  const on = dests.filter((d) => d.enabled)
  return (
    <div data-testid="chats" className="flex min-h-0 flex-1 flex-col">
      <WatchStrip dests={dests} now={now} onOutputs={onOutputs} onFix={onFix} />
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-3">
        <div data-testid="chats-soon" className="flex flex-col gap-1 rounded-md border border-dashed border-line-2 p-3">
          <span className="flex items-center gap-2 text-sm font-medium"><ChatIcon />{t('chats.title')}<Soon /></span>
          <span className="text-xs text-text-soft">{t('chats.body')}</span>
        </div>
        {on.length === 0 ? <p className="m-0 text-[13px] text-muted">{t('chats.noOutputs')}</p> : (
          <ul className="m-0 flex list-none flex-col p-0" aria-label={t('chats.access')}>
            {on.map((d) => {
              const a = chatAccess(d, accounts)
              const p = d.platform === 'youtube' || d.platform === 'twitch' ? d.platform : undefined
              return (
                <li key={d.id} data-testid={`chat-access-${d.id}`} data-access={a} className="flex items-center gap-2.5 border-b border-line py-2 last:border-0">
                  <LogoSlot platform={d.platform} />
                  <span className="min-w-0 flex-1 truncate text-[13px]">{d.name}</span>
                  {a === 'reads' && <span className="inline-flex items-center gap-1 font-mono text-[10px] font-semibold tracking-[.08em] text-live"><Icon name="check" size={12} />{t('chats.reads')}</span>}
                  {a === 'connect' && p && (
                    <button type="button" disabled={!!connecting} onClick={() => onConnect(p)} className="min-h-8 text-xs text-accent underline-offset-2 hover:underline disabled:text-faint max-md:min-h-[44px]">
                      {connecting === p ? t('chats.opening', { platform: d.profileName }) : <span className="inline-flex items-center gap-1">{t('chats.connect')}<Icon name="external" size={12} /></span>}
                    </button>
                  )}
                  {a === 'unsupported' && <span className="text-[11px] text-muted">{d.platform === 'instagram' ? t('chats.instagram') : t('chats.noAccess')}</span>}
                  {a === 'unknown' && <span className="text-[11px] text-muted">—</span>}
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}

export function Soon() {
  return <span className="rounded-sm border border-line-2 px-1 font-mono text-[9px] font-semibold tracking-[.08em] text-muted">{t('common.soon').toUpperCase()}</span>
}

/** Column head of a panel that replaced a column (Input, Add output, an output's settings). */
export function PanelHead({ title, onBack, onClose, testId }: { title: ReactNode; onBack?: () => void; onClose?: () => void; testId?: string }) {
  return (
    <div data-testid={testId} className="flex min-h-11 flex-none items-center gap-2 border-b border-line px-3">
      {onBack && <Button variant="ghost" size="sm" icon onClick={onBack} aria-label={t('common.back')} className="max-md:h-11 max-md:w-11"><Icon name="chevron-right" size={16} className="rotate-180" /></Button>}
      <span className="min-w-0 flex-1 truncate font-mono text-[11px] font-semibold tracking-[.12em] text-accent">{title}</span>
      {onClose && <Button variant="ghost" size="sm" icon onClick={onClose} aria-label={t('common.close')} className="max-md:h-11 max-md:w-11"><Icon name="close" size={16} /></Button>}
    </div>
  )
}
